<template>
  <div class="datepicker-wrapper" :class="{ 'datepicker-clearable': clearButton }">
    <flat-pickr :value="displayValue" :config="flatpickrConfig" :class="scssProps" @on-change="onChange"></flat-pickr>
    <i v-if="clearButton && value" class="vdp-datepicker__clear-button" @click="clear">×</i>
  </div>
</template>

<script>
// Built on flatpickr rather than vuejs-datepicker, which
// renders its calendar popup as a plain absolutely-positioned sibling nested in this component's
// own DOM, with no "append to body"/portal/auto-flip option — any ancestor with `overflow` other
// than `visible` (a scrollable filter sidebar, in particular — see FiltersPanel.vue's
// `.search-new-fsb-body`) clips it outright, no z-index can escape that. flatpickr's `appendTo`
// config teleports the calendar to an arbitrary DOM node (here, <body>) at open time, sidestepping
// the whole class of overflow/z-index/stacking-context problems this used to cause. It also
// supports typing a date manually out of the box (`allowInput`), which vuejs-datepicker didn't.
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { Italian } from 'flatpickr/dist/l10n/it.js'
import { english as English } from 'flatpickr/dist/l10n/default.js'

export default {
  components: {
    FlatPickr,
  },
  props: {
    value: { type: Date },
    format: { type: String, default: 'short' },
    variant: { type: String, default: 'blue' },
    disabledDates: {
      type: Object,
      default: () => ({}),
    },
    // Opt-in, defaults to false to leave every existing consumer's look unchanged. Clearing still
    // emits `update:value` with null regardless of this flag — it only controls whether the "×"
    // clear affordance is rendered.
    clearButton: { type: Boolean, default: false },
  },
  computed: {
    // vue-flatpickr-component's internal `value` watcher decides whether to push a change into
    // the underlying flatpickr instance by comparing the incoming value against the input's
    // CURRENTLY DISPLAYED text (`newValue === nullify(this.$el.value)`, both expected to be
    // strings). Feeding it a `Date` object directly broke that guard in two ways: (1) a Date is
    // never `===` a string, so the guard never short-circuits, and (2) callers here (e.g.
    // RangeFilter.vue's `dateFrom` getter) construct a NEW `Date` instance on every read, so even
    // comparing Date-to-Date by reference would never match. Every single external value update
    // therefore called `fp.setDate(value, true)`, whose `true` (triggerChange) re-fires our own
    // `onChange` handler with the same date, which re-emits `update:value`, which flows back down
    // as yet another new value — an unbounded synchronous loop, hanging the tab ("page isn't
    // responding") the moment a day was picked. Passing a plain, primitively-comparable STRING
    // here instead — formatted exactly like the input's own displayed text — lets that guard
    // actually match once the display settles, breaking the loop after a single round-trip.
    displayValue() {
      return this.value ? this.$d(this.value, this.format) : null
    },
    scssProps() {
      const props = 'custom-select-sm form-control'
      return this.variant === 'blue' ? props : props + ' bg-white'
    },
    // vuejs-datepicker's `disabledDates.from`/`.to` disabled everything on/after `from`, or
    // on/before `to` (either can be set alone or together) — flatpickr's `disable` option instead
    // takes a list of {from, to} RANGES to disable, so each bound becomes its own open-ended range
    // against the Date type's actual min/max, matching the exact same semantics.
    disable() {
      const ranges = []
      if (this.disabledDates.from) {
        ranges.push({ from: this.disabledDates.from, to: new Date(8640000000000000) })
      }
      if (this.disabledDates.to) {
        ranges.push({ from: new Date(-8640000000000000), to: this.disabledDates.to })
      }
      return ranges
    },
    flatpickrConfig() {
      return {
        allowInput: true,
        appendTo: document.body,
        disable: this.disable,
        locale: this.$i18n.locale === 'en' ? English : Italian,
        // Overrides flatpickr's own display formatting entirely, so the input still reads via
        // this app's own i18n date formats (vue-i18n's $d, keyed by the same `format` prop the
        // previous implementation used), instead of flatpickr's dateFormat token syntax.
        formatDate: (date) => this.$d(date, this.format),
        // Accepts what the user types on blur/Enter, matching the exact layout `formatDate`
        // above displays (both locales' "short" dateTimeFormat: 2-digit day, 2-digit month,
        // numeric year — see locales/{en,it}.json — rendered by Intl with "/" separators, in
        // day-first order for it and month-first for en). `new Date(datestr)` can't be used
        // here: its built-in parsing always reads slash-separated dates as MM/DD/YYYY
        // regardless of locale, silently transposing day and month whenever an `it` user types
        // e.g. "25/03/2026". Parsed explicitly instead of guessing.
        parseDate: (datestr) => {
          const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(datestr)
          if (!match) {
            return undefined
          }
          const [, first, second, year] = match.map(Number)
          const [day, month] = this.$i18n.locale === 'en' ? [second, first] : [first, second]
          const parsed = new Date(year, month - 1, day)
          return isNaN(parsed) || parsed.getMonth() !== month - 1 ? undefined : parsed
        },
      }
    },
  },
  methods: {
    // flatpickr's own `onChange` hook (mapped by vue-flatpickr-component to the `on-change`
    // event) hands back real parsed Date objects — unlike the component's `v-model`/`input`
    // event, which instead round-trips the DISPLAYED string (run through our own `formatDate`,
    // so not machine-parseable) as `event.target.value`. Using v-model here would crash
    // downstream consumers expecting a Date (RangeFilter.vue's `toIsoDate` calling
    // `date.toISOString()` on what would be a formatted string).
    onChange(selectedDates) {
      this.$emit('update:value', selectedDates[0] || null)
    },
    clear() {
      this.$emit('update:value', null)
    },
  },
}
</script>

<style lang="scss" scoped>
.datepicker-wrapper {
  position: relative;
}

// Only ever applied when clearButton is on — every other consumer (Perimeter's date pickers)
// never gets this class, unaffected.
.datepicker-clearable {
  .vdp-datepicker__clear-button {
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    color: #6b7280;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;

    &:hover {
      color: #374151;
    }
  }

  ::v-deep input {
    padding-right: 24px;
  }
}
</style>
